import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Target, Eye, Award, Users, TrendingUp, Globe } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Award className="w-10 h-10" />,
      title: 'Excellence',
      description: 'We strive for excellence in every solution we deliver'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Customer Focus',
      description: 'Your success is our priority, always'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Innovation',
      description: 'Constantly evolving with cutting-edge technology'
    },
    {
      icon: <Globe className="w-10 h-10" />,
      title: 'Global Reach',
      description: 'Serving clients across multiple industries worldwide'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16 px-4">
        <div className="container mx-auto max-w-7xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About Sight Vision</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Pioneering supply chain solutions with innovative technology
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Who We Are</h2>
              <p className="text-lg text-slate-600 mb-4">
                Sight Vision IT Solution Pvt Ltd is a leading provider of comprehensive supply chain management solutions. With over a decade of experience, we specialize in Warehouse Management Systems (WMS), Work in Progress (WIP) tracking, inventory management, and asset tracking.
              </p>
              <p className="text-lg text-slate-600 mb-4">
                Our solutions leverage cutting-edge technology including mobile applications, barcode scanning, IoT integration, and RFID technology to help businesses optimize their operations and achieve unprecedented efficiency.
              </p>
              <p className="text-lg text-slate-600">
                We pride ourselves on delivering tailored solutions that meet the unique needs of each client, backed by 24/7 support and continuous innovation.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-0 shadow-lg bg-blue-600 text-white">
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold mb-2">10+</div>
                  <p className="text-sm">Years Experience</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-cyan-600 text-white">
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold mb-2">50+</div>
                  <p className="text-sm">Projects Delivered</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-slate-700 text-white">
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold mb-2">200+</div>
                  <p className="text-sm">Happy Clients</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-blue-700 text-white">
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold mb-2">50+</div>
                  <p className="text-sm">Team Members</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Target className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-2xl">Our Mission</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-lg">
                  To empower businesses with innovative supply chain solutions that drive efficiency, reduce costs, and enhance operational visibility. We are committed to delivering world-class technology solutions that transform the way organizations manage their supply chains.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="bg-cyan-100 p-3 rounded-lg">
                    <Eye className="w-8 h-8 text-cyan-600" />
                  </div>
                  <CardTitle className="text-2xl">Our Vision</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-lg">
                  To be the global leader in supply chain technology solutions, recognized for our innovation, reliability, and customer-centric approach. We envision a future where every business, regardless of size, has access to enterprise-grade supply chain management tools.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="pt-6 text-center">
                  <div className="text-blue-600 flex justify-center mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Us</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              What sets us apart in the industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-3">Proven Expertise</h3>
              <p className="text-slate-300">
                Over 10 years of experience delivering successful supply chain solutions across diverse industries
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-3">Cutting-Edge Technology</h3>
              <p className="text-slate-300">
                We leverage the latest in IoT, RFID, mobile, and cloud technologies to keep you ahead
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-3">Dedicated Support</h3>
              <p className="text-slate-300">
                24/7 technical support and ongoing consultation to ensure your continued success
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;